import { isDefined, isString, isObject } from '../guards/index.js';
import * as levenshtein from 'fastest-levenshtein';

const URLRegex = /^(https?:\/\/)?([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9-1_]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/;
const simpleHash = (text) => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = (hash << 5) - hash + text.charCodeAt(i);
    hash |= 0;
  }
  return hash;
};
const simpleHashUint8Array = (data) => {
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    hash = (hash << 5) - hash + data[i];
    hash |= 0;
  }
  return hash;
};
const simpleHashString = (data) => {
  const encoder = new TextEncoder();
  const buffer = encoder.encode(data);
  return simpleHashUint8Array(buffer).toString(36);
};
const trimChars = (text, chars) => {
  let start = 0, end = text.length;
  while (start < end && chars.includes(text[start])) ++start;
  while (end > start && chars.includes(text[end - 1])) --end;
  return start > 0 || end < text.length ? text.substring(start, end) : text;
};
const boolToYesOrNo = (bool) => {
  return bool ? "Yes" : "No";
};
const createIndent = (level) => "  ".repeat(level);
function camelCaseToText(str) {
  const words = str.split(/(?=[A-Z])/).map((word) => word.toLowerCase());
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
  return words.join(" ");
}
const prettifyForLLM = (rootObj) => {
  const recursive = (obj, level = 0) => {
    if (!isDefined(obj)) return "";
    const indent = createIndent(level);
    const stringIndent = createIndent(level + 1);
    if (isString(obj)) return obj.split("\n").map((v, i) => i === 0 ? v : `${stringIndent}${v}`).join("\n");
    if (typeof obj === "number" || typeof obj === "boolean") return obj.toString();
    if (Array.isArray(obj)) {
      if (obj.length === 0) return "";
      return obj.map((item, index) => {
        const itemStr = recursive(item, level + 1);
        return itemStr ? `
${indent}${(index + 1).toString()}. ${itemStr}` : "";
      }).join("");
    }
    if (isObject(obj)) {
      const entries = Object.entries(obj);
      if (entries.length === 0) return "";
      return entries.map(([key, value]) => {
        const valueStr = recursive(value, level + 1);
        return valueStr ? `
${indent}${level > 0 ? "- " : ""}${camelCaseToText(key)}: ${valueStr}` : "";
      }).join("");
    }
    return "";
  };
  return trimChars(recursive(rootObj), ["\n"]);
};
function addHyphenationHints(str, n) {
  let output = "";
  let count = 0;
  for (const char of str) {
    output += char;
    if (/[a-zA-Z]/.test(char)) {
      count++;
      if (count === n) {
        output += "­";
        count = 0;
      }
    } else {
      count = 0;
    }
  }
  return output;
}
const getTextWidth = (text, font) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return 0;
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
};
const calculateTruncationPoints = (text, maxRows, ellipsis, availableWidth, font, offsetBefore, keepLastChars) => {
  let currentRow = 1;
  const start = 0;
  let end = text.length - keepLastChars - ellipsis.length;
  const totalLastCharsWidth = getTextWidth(text.substring(text.length - keepLastChars), font) + getTextWidth(ellipsis, font);
  let currentWidth = offsetBefore + (currentRow >= maxRows ? totalLastCharsWidth : 0);
  const charSizes = [];
  for (let i = 0; i < end; i++) {
    const charWidth = getTextWidth(text[i], font);
    charSizes.push(charWidth);
    if (currentWidth + charWidth >= availableWidth) {
      if (currentRow === maxRows) {
        let totalWidth = 0;
        let j = i;
        while (totalWidth < totalLastCharsWidth && j >= 0) {
          totalWidth += charSizes[j];
          j--;
        }
        end = Math.max(0, j + 1);
        break;
      }
      currentRow++;
      currentWidth = currentRow >= maxRows ? totalLastCharsWidth : 0;
    }
    currentWidth += charWidth;
  }
  return {
    start,
    end
  };
};
const constructTruncatedText = (text, start, end, ellipsis, keepLastChars) => {
  const frontPart = text.substring(start, end);
  const lastPart = text.substring(text.length - keepLastChars);
  return `${frontPart}${ellipsis}${lastPart}`;
};
const truncateTextWithinElement = (text, options) => {
  if (!options.element && !options.targetWidth) return text;
  const {
    maxRows,
    ellipsis,
    offsetBefore = 0,
    keepLastChars = 0,
    targetWidth,
    element
  } = options;
  const actualWidth = element ? element.offsetWidth : targetWidth ?? 0;
  const font = element ? `${window.getComputedStyle(element).fontSize} ${window.getComputedStyle(element).fontFamily}` : "16px Arial";
  if (getTextWidth(text, font) + offsetBefore <= actualWidth * maxRows) {
    return text;
  }
  const {
    start,
    end
  } = calculateTruncationPoints(text, maxRows, ellipsis, actualWidth, font, offsetBefore, keepLastChars);
  return constructTruncatedText(text, start, end, ellipsis, keepLastChars);
};
const capitalizeFirstLetter = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
const capitalizeFirstWordIfLowercase = (str) => {
  if (str.length === 0 || str.toLocaleLowerCase() !== str) return str;
  const [firstWord, ...rest] = str.split(" ");
  return capitalizeFirstLetter(firstWord) + (rest.length > 0 ? " " + rest.join(" ") : "");
};
const noteTextFormat = (text) => {
  return text.replace(/^(?:[\t ]*(?:\r?\n|\r))+/gm, "").replace(/^\s+|\s+$/gm, "").trim();
};
const stringToRegExp = (str, i = true) => {
  if (!str) return str ?? "";
  const regexpString = str.replace(/[|\\{}()[\]^$+*?.]/g, "\\$&");
  return new RegExp(regexpString, i ? "i" : "");
};
const snakeCaseToWords = (text, options) => {
  const words = text.split("_").map((word) => word.toLowerCase()).map((word) => word.charAt(0).toUpperCase() + word.slice(1));
  if (options?.capitalize === "first") {
    words[0] = words[0].toLowerCase();
  }
  return words.join(" ");
};
const trimStartChars = (text, chars) => {
  let start = 0;
  while (start < text.length && chars.includes(text[start])) ++start;
  return start > 0 ? text.substring(start) : text;
};
const truncateText = (text, length, ellipsis = "…") => {
  return text.length > length ? text.slice(0, length) + ellipsis : text;
};
const decodeHTMLEntities = (text) => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};
const obfuscateText = (text) => {
  if (!text) return text;
  return text.replace(/[a-zA-Z]/g, (char) => char === char.toUpperCase() ? "X" : "x").replace(/\d/g, "0");
};
const AVERAGE_SILENT_WPM = 200;
const calculateReadingTime = (text) => {
  let wordCount = 0;
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    const segmenter = new Intl.Segmenter("en", {
      granularity: "word"
    });
    const segments = segmenter.segment(text)[Symbol.iterator]();
    for (const segment of segments) {
      if (segment.isWordLike) {
        wordCount++;
      }
    }
  } else {
    wordCount = text.split(/\s+/).filter(Boolean).length;
  }
  const minutes = wordCount / AVERAGE_SILENT_WPM;
  return Math.ceil(minutes * 60);
};
const NORMALIZATION_MAPS = {
  SMART_QUOTES: [[/\u201C/g, '"'], [/\u201D/g, '"'], [/\u2018/g, "'"], [/\u2019/g, "'"]],
  TYPOGRAPHIC: [[/\u2026/g, "..."], [/\u2014/g, "-"], [/\u2013/g, "-"], [/\u00A0/g, " "]]
};
function normalizeString(str) {
  let normalized = str;
  for (const [smart, regular] of NORMALIZATION_MAPS.SMART_QUOTES) {
    normalized = normalized.replace(smart, regular);
  }
  for (const [typographic, regular] of NORMALIZATION_MAPS.TYPOGRAPHIC) {
    normalized = normalized.replace(typographic, regular);
  }
  normalized = normalized.replace(/\s+/g, " ");
  return normalized.trim();
}
function getSimilarity(original, search) {
  if (search === "") {
    return 0;
  }
  const normalizedOriginal = normalizeString(original);
  const normalizedSearch = normalizeString(search);
  if (normalizedOriginal === normalizedSearch) {
    return 1;
  }
  const dist = levenshtein.distance(normalizedOriginal, normalizedSearch);
  const maxLength = Math.max(normalizedOriginal.length, normalizedSearch.length);
  return 1 - dist / maxLength;
}

export { URLRegex, addHyphenationHints, boolToYesOrNo, calculateReadingTime, capitalizeFirstLetter, capitalizeFirstWordIfLowercase, decodeHTMLEntities, getSimilarity, normalizeString, noteTextFormat, obfuscateText, prettifyForLLM, simpleHash, simpleHashString, simpleHashUint8Array, snakeCaseToWords, stringToRegExp, trimChars, trimStartChars, truncateText, truncateTextWithinElement };
//# sourceMappingURL=index.js.map
